import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";
import { PageHero } from "@/components/site/PageHero";

export default function News() {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "News | LaunchLab";
    api.get("/news").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <div data-testid="news-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <PageHero
        testid="news-hero"
        kicker="LaunchLab Newsroom"
        title="ANNOUNCEMENTS &"
        highlight="MILESTONES."
        subtext="New projects, partnerships, launches and important updates from the LaunchLab ecosystem."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {items === null ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center text-slate-400" data-testid="news-empty">No announcements yet.</div>
        ) : (
          <div className="space-y-5 max-w-4xl mx-auto" data-testid="news-list">
            {items.map((n, i) => (
              <motion.button
                key={n.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                onClick={() => setSelected(n)}
                data-testid={`news-card-${n.id}`}
                className="card-beam group w-full text-left glass rounded-2xl p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#FF8800]/40 flex items-start gap-5"
              >
                <div className="hidden sm:flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-[#FF8800]/10 border border-[#FF8800]/25 shrink-0">
                  <span className="font-display font-extrabold text-lg text-[#FF8800]">{n.date ? new Date(n.date).getDate() : "—"}</span>
                  <span className="font-mono-tech text-[9px] tracking-widest text-slate-400 uppercase">
                    {n.date ? new Date(n.date).toLocaleDateString("en-IN", { month: "short" }) : ""}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono-tech text-[10px] tracking-[0.18em] text-[#00B0FF] uppercase">{n.category}</span>
                    <span className="text-xs text-slate-500 flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      {n.date ? new Date(n.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : ""}
                    </span>
                  </div>
                  <h3 className="mt-2 font-display text-lg sm:text-xl font-semibold text-white leading-snug group-hover:text-[#FF8800] transition-colors">
                    {n.headline}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-2">{n.summary}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-[#FF8800] transition-colors shrink-0 mt-1.5" />
              </motion.button>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-[#0D111D] border-white/10 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="news-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogDescription className="sr-only">{selected.headline}</DialogDescription>
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <span className="glass px-3 py-1 rounded-full font-mono-tech text-[10px] tracking-[0.16em] text-[#00B0FF] uppercase">{selected.category}</span>
                  <span className="text-xs text-slate-500">
                    {selected.date ? new Date(selected.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : ""}
                  </span>
                </div>
                <DialogTitle className="font-display text-xl sm:text-2xl font-bold text-white text-left leading-snug" data-testid="news-detail-title">
                  {selected.headline}
                </DialogTitle>
              </DialogHeader>
              {selected.image && (
                <img src={selected.image} alt={selected.headline} className="mt-4 rounded-xl w-full h-auto object-cover max-h-72" />
              )}
              <div className="mt-5 space-y-4" data-testid="news-detail-content">
                {(selected.content || selected.summary || "").split(/\n\s*\n/).filter(Boolean).map((p, i) => (
                  <p key={i} className="text-sm sm:text-base text-slate-300 leading-relaxed">{p}</p>
                ))}
              </div>
              {selected.external_link && (
                <a
                  href={selected.external_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="news-detail-external-link"
                  className="mt-6 inline-flex items-center gap-2 text-sm text-[#FF8800] hover:underline"
                >
                  Read more <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
